import { addDoc, collection, getFirestore, Timestamp } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { useState } from 'react';
import { app } from '../firebase';
import './UploadPage.css';

const db = getFirestore(app);
const storage = getStorage(app);

const getAudioContentType = (file) => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const fallbackTypes = {
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    flac: 'audio/flac',
    aac: 'audio/aac',
  };

  return file.type || fallbackTypes[extension] || 'audio/mpeg';
};

const getImageContentType = (file) => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const fallbackTypes = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
  };

  return file.type || fallbackTypes[extension] || 'image/jpeg';
};

const UploadPage = ({ user }) => {
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [trackName, setTrackName] = useState('');
  const [genre, setGenre] = useState('');
  const [price, setPrice] = useState('');
  const [audioDragActive, setAudioDragActive] = useState(false);
  const [coverDragActive, setCoverDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isUploading, setIsUploading] = useState(false);


  const handleAudioFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setAudioFile(selectedFile);
      setAudioDragActive(false);
    }
  };

  const handleCoverFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setCoverFile(selectedFile);
      setCoverDragActive(false);
    }
  };

  const handleAudioDragOver = (e) => {
    e.preventDefault();
    setAudioDragActive(true);
  };

  const handleAudioDragLeave = () => {
    setAudioDragActive(false);
  };

  const handleAudioDrop = (e) => {
    e.preventDefault();
    setAudioDragActive(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.type.startsWith('audio/') || /\.(mp3|m4a|wav|ogg|flac|aac)$/i.test(droppedFile.name))) {
      setAudioFile(droppedFile);
    }
  };

  const handleCoverDragOver = (e) => {
    e.preventDefault();
    setCoverDragActive(true);
  };

  const handleCoverDragLeave = () => {
    setCoverDragActive(false);
  };

  const handleCoverDrop = (e) => {
    e.preventDefault();
    setCoverDragActive(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      setCoverFile(droppedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');
    
    if (!user) {
      setErrorMsg('You must be logged in to upload.');
      return;
    }
    
    if (!audioFile || !coverFile) {
      setErrorMsg('Please upload both audio file and cover art.');
      return;
    }
    
    if (!trackName.trim()) {
      setErrorMsg('Please enter a track name.');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      // Upload cover image first
      const coverFileName = `${Date.now()}_${coverFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const coverRef = ref(storage, `covers/${user.uid}/${coverFileName}`);
      const coverUploadTask = uploadBytesResumable(coverRef, coverFile, {
        contentType: getImageContentType(coverFile),
      });
      
      await new Promise((resolve, reject) => {
        coverUploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setUploadProgress(progress);
          },
          (error) => {
            console.error('Cover upload error:', error);
            const errorMsg = error.code === 'storage/unauthorized' 
              ? 'Permission denied. Check Firebase Storage rules.'
              : error.code === 'storage/canceled'
              ? 'Upload canceled.'
              : `Failed to upload cover image: ${error.message}`;
            setErrorMsg(errorMsg);
            setIsUploading(false);
            setUploadProgress(0);
            reject(error);
          },
          async () => {
            try {
              const coverURL = await getDownloadURL(coverUploadTask.snapshot.ref);
              
              // Upload audio file
              const audioFileName = `${Date.now()}_${audioFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
              const audioRef = ref(storage, `tracks/${user.uid}/${audioFileName}`);
              const audioUploadTask = uploadBytesResumable(audioRef, audioFile, {
                contentType: getAudioContentType(audioFile),
              });
              
              audioUploadTask.on('state_changed',
                (snapshot) => {
                  const progress = 50 + Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
                  setUploadProgress(progress);
                },
                (error) => {
                  console.error('Audio upload error:', error);
                  const errorMsg = error.code === 'storage/unauthorized'
                    ? 'Permission denied. Check Firebase Storage rules.'
                    : error.code === 'storage/canceled'
                    ? 'Upload canceled.'
                    : `Failed to upload audio file: ${error.message}`;
                  setErrorMsg(errorMsg);
                  setIsUploading(false);
                  setUploadProgress(0);
                  reject(error);
                },
                async () => {
                  try {
                    const audioURL = await getDownloadURL(audioUploadTask.snapshot.ref);
                    
                    // Save to Firestore
                    const trackData = {
                      title: trackName.trim(),
                      fileUrl: audioURL,
                      coverImage: coverURL,
                      userId: user.uid,
                      artist: user.displayName || user.email || 'Unknown Artist',
                      genre: genre.trim() || 'Uncategorized',
                      price: parseFloat(price) || 0,
                      uploadedAt: Timestamp.now(),
                    };
                    
                    await addDoc(collection(db, 'tracks'), trackData);
                    
                    setSuccessMsg('Beat uploaded successfully!');
                    setUploadProgress(100);
                    
                    // Reset form after a brief delay
                    setTimeout(() => {
                      setAudioFile(null);
                      setCoverFile(null);
                      setTrackName('');
                      setGenre('');
                      setPrice('');
                      setUploadProgress(0);
                      setIsUploading(false);
                      
                      // Reset file inputs
                      const audioInput = document.getElementById('audio-file');
                      const coverInput = document.getElementById('cover-file');
                      if (audioInput) audioInput.value = '';
                      if (coverInput) coverInput.value = '';
                    }, 1000);
                    
                    // Clear success message after 5 seconds
                    setTimeout(() => {
                      setSuccessMsg('');
                    }, 5000);
                    
                    resolve();
                  } catch (error) {
                    console.error('Firestore error:', error);
                    const errorMsg = error.code === 'permission-denied'
                      ? 'Permission denied. Check Firestore security rules.'
                      : `Failed to save track: ${error.message}`;
                    setErrorMsg(errorMsg);
                    setIsUploading(false);
                    setUploadProgress(0);
                    reject(error);
                  }
                }
              );
            } catch (error) {
              console.error('Cover URL error:', error);
              setErrorMsg('Failed to get cover image URL: ' + error.message);
              setIsUploading(false);
              setUploadProgress(0);
              reject(error);
            }
          }
        );
      });
    } catch (err) {
      console.error('Upload error:', err);
      const errorMsg = err.code === 'storage/unauthorized' || err.code === 'permission-denied'
        ? 'Permission denied. Please check Firebase Storage and Firestore rules.'
        : `Upload failed: ${err.message || 'Unknown error'}`;
      setErrorMsg(errorMsg);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  if (!user) {
    return (
      <div className="upload-page">
        <div className="upload-header">
          <h1>Upload Your Beat</h1>
          <p className="upload-subtitle">Share your music with the world and start earning.</p>
        </div>
        <div className="upload-login-prompt">
          <div className="upload-login-message">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 17C11.45 17 11 16.55 11 16V12C11 11.45 11.45 11 12 11C12.55 11 13 11.45 13 12V16C13 16.55 12.55 17 12 17ZM13 9H11V7H13V9Z" fill="currentColor"/>
            </svg>
            <h2>Please log in to upload beats</h2>
            <p>You need to be logged in to upload your music to Tune.</p>
            <p className="upload-login-hint">Use the Log In or Sign Up buttons in the header to get started.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="upload-page">
      <div className="upload-header">
        <h1>Upload Your Beat</h1>
        <p className="upload-subtitle">Share your music with the world and start earning.</p>
      </div>

      <form className="upload-form" onSubmit={handleSubmit}>
        {errorMsg && <div className="upload-error">{errorMsg}</div>}
        {successMsg && <div className="upload-success">{successMsg}</div>}

        {/* Track Name */}
        <div className="form-group">
          <label htmlFor="track-name">Track Name <span className="required">*</span></label>
          <input
            type="text"
            id="track-name"
            value={trackName}
            onChange={(e) => setTrackName(e.target.value)}
            placeholder="Enter track name"
            required
            disabled={isUploading}
          />
        </div>

        {/* Genre */}
        <div className="form-group">
          <label htmlFor="genre">Genre</label>
          <input
            type="text"
            id="genre"
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            placeholder="Enter genre (optional)"
            disabled={isUploading}
          />
        </div>

        {/* Price */}
        <div className="form-group">
          <label htmlFor="price">Price (USD)</label>
          <input
            type="number"
            id="price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="Enter price (optional, leave blank for Free)"
            min="0"
            step="0.01"
            disabled={isUploading}
          />
        </div>

        {/* Audio File Upload */}
        <div className="upload-section">
          <div className="upload-section-header">
            <div className="upload-section-title">
              <svg className="upload-section-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2V10M10 10L6 6M10 10L14 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12H18M2 16H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Audio File</span>
            </div>
            <span className="required-asterisk">*</span>
          </div>
          
          <div
            className={`upload-drop-zone ${audioDragActive ? 'active' : ''} ${audioFile ? 'has-file' : ''}`}
            onDragOver={handleAudioDragOver}
            onDragLeave={handleAudioDragLeave}
            onDrop={handleAudioDrop}
          >
            <input
              type="file"
              id="audio-file"
              accept="audio/*"
              onChange={handleAudioFileChange}
              required
            />
            
            {audioFile ? (
              <div className="file-selected">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p className="file-name">{audioFile.name}</p>
                <p className="file-size">{(audioFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            ) : (
              <>
                <svg className="upload-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18V12L5 16M9 12L13 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 12H21M3 16H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <p className="drop-zone-text">Drop your audio file here or click to browse</p>
                <p className="file-requirements">MP3, WAV, FLAC (Max 100MB)</p>
              </>
            )}
          </div>
        </div>

        {/* Cover Art Upload */}
        <div className="upload-section">
          <div className="upload-section-header">
            <div className="upload-section-title">
              <svg className="upload-section-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                <path d="M3 8L7 4L11 8L17 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="6" cy="12" r="1.5" fill="currentColor"/>
              </svg>
              <span>Cover Art</span>
            </div>
            <span className="required-asterisk">*</span>
          </div>
          
          <div
            className={`upload-drop-zone ${coverDragActive ? 'active' : ''} ${coverFile ? 'has-file' : ''}`}
            onDragOver={handleCoverDragOver}
            onDragLeave={handleCoverDragLeave}
            onDrop={handleCoverDrop}
          >
            <input
              type="file"
              id="cover-file"
              accept="image/*"
              onChange={handleCoverFileChange}
              required
            />
            
            {coverFile ? (
              <div className="file-selected">
                <img src={URL.createObjectURL(coverFile)} alt="Cover preview" className="cover-preview" />
                <p className="file-name">{coverFile.name}</p>
                <p className="file-size">{(coverFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            ) : (
              <>
                <svg className="upload-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M3 9L9 3L15 9L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="6" cy="15" r="1.5" fill="currentColor"/>
                </svg>
                <p className="drop-zone-text">Upload cover image</p>
                <p className="file-requirements">JPG, PNG (Min 1000x1000px)</p>
              </>
            )}
          </div>
        </div>

        {uploadProgress > 0 && uploadProgress < 100 && (
          <div className="progress-container">
            <div className="progress-bar">
              <div className="progress" style={{ width: `${uploadProgress}%` }}></div>
            </div>
            <p className="progress-text">Uploading... {uploadProgress}%</p>
          </div>
        )}

        <button
          type="submit"
          className="upload-submit-button"
          disabled={isUploading || !audioFile || !coverFile}
        >
          {isUploading ? 'Uploading...' : 'Upload Beat'}
        </button>
      </form>
    </div>
  );
};

export default UploadPage;
